import WarningAmberOutlined from "@mui/icons-material/WarningAmberOutlined";
import { Box, Button, DialogActions, DialogContent, Typography } from "@mui/material";

import { CoAppDialog } from "@/components/global/styled/global.styled";

/** Full-screen overlay tint used with this modal (Future Designs backdrop layer). */
const MODAL_BACKDROP = "rgba(3, 40, 60, 0.5)";

/** Figma: header strip + primary CTA (node 8291:140316). */
const HEADER_BG = "#fff5ac";
const SAVE_BUTTON_BG = "#0072ae";
const OUTLINED_INFO_TEXT = "#0195d5";
const OUTLINED_INFO_BORDER = "rgba(1, 149, 213, 0.5)";

const PAPER_SHADOW =
	"0px 6px 30px 0px rgba(0, 0, 0, 0.12), 0px 16px 24px 0px rgba(0, 0, 0, 0.14), 0px 8px 10px 0px rgba(0, 0, 0, 0.2)";

const BUTTON_LABEL_SX = {
	textTransform: "none" as const,
	fontWeight: 600,
	fontSize: 14,
	lineHeight: "24px",
	letterSpacing: "0.4px",
	borderRadius: "20px",
	px: 2,
	py: 0.75
};

export interface UnsavedChangesModalProps {
	open: boolean;
	/** Backdrop click and Escape — stay on the page. */
	onClose: () => void;
	onExitWithoutSaving: () => void;
	onSaveAndContinue: () => void | Promise<void>;
	isSaving?: boolean;
	message?: string;
}

const DEFAULT_MESSAGE =
	"You have unsaved changes. Are you sure you want to navigate away without saving?";

export default function UnsavedChangesModal(props: UnsavedChangesModalProps) {
	const {
		open,
		onClose,
		onExitWithoutSaving,
		onSaveAndContinue,
		isSaving = false,
		message = DEFAULT_MESSAGE
	} = props;

	return (
		<CoAppDialog
			open={open}
			onClose={onClose}
			maxWidth={false}
			slotProps={{
				backdrop: {
					sx: { backgroundColor: MODAL_BACKDROP }
				},
				paper: {
					sx: {
						borderRadius: "12px",
						overflow: "hidden",
						width: "100%",
						maxWidth: 714,
						boxShadow: PAPER_SHADOW,
						margin: 2,
						height: 300
					}
				}
			}}
		>
			<Box
				sx={{
					bgcolor: HEADER_BG,
					display: "flex",
					alignItems: "center",
					gap: 2,
					px: 4.5,
					py: 3,
					borderTopLeftRadius: 12,
					borderTopRightRadius: 12
				}}
			>
				<WarningAmberOutlined sx={{ fontSize: 38, color: "text.primary" }} aria-hidden />
				<Typography
					component="h2"
					variant="h6"
					sx={{
						fontWeight: 500,
						fontSize: 20,
						lineHeight: 1.6,
						letterSpacing: "0.15px",
						color: "text.primary"
					}}
				>
					Unsaved Changes
				</Typography>
			</Box>

			<DialogContent sx={{ pt: 3, pb: 1, px: 4.5 }}>
				<Typography
					component="p"
					variant="h6"
					sx={{
						fontWeight: 500,
						fontSize: 20,
						lineHeight: 1.6,
						letterSpacing: "0.15px",
						color: "text.primary"
					}}
				>
					{message}
				</Typography>
			</DialogContent>

			<DialogActions
				sx={{
					px: 3,
					pb: 3,
					pt: 0,
					gap: 1.5,
					justifyContent: "flex-end",
					flexWrap: "wrap"
				}}
			>
				<Button
					variant="outlined"
					size="medium"
					onClick={onExitWithoutSaving}
					disabled={isSaving}
					sx={{
						...BUTTON_LABEL_SX,
						borderColor: OUTLINED_INFO_BORDER,
						color: OUTLINED_INFO_TEXT,
						"&:hover": {
							borderColor: OUTLINED_INFO_BORDER,
							backgroundColor: "action.hover"
						}
					}}
				>
					Exit Without Saving
				</Button>
				<Button
					variant="contained"
					disableElevation
					onClick={() => {
						void onSaveAndContinue();
					}}
					disabled={isSaving}
					sx={{
						...BUTTON_LABEL_SX,
						backgroundColor: SAVE_BUTTON_BG,
						color: "#fff",
						boxShadow:
							"0px 1px 5px 0px rgba(0, 0, 0, 0.12), 0px 2px 2px 0px rgba(0, 0, 0, 0.14), 0px 3px 1px -2px rgba(0, 0, 0, 0.2)",
						"&:hover": {
							backgroundColor: "#006199",
							boxShadow:
								"0px 1px 5px 0px rgba(0, 0, 0, 0.12), 0px 2px 2px 0px rgba(0, 0, 0, 0.14), 0px 3px 1px -2px rgba(0, 0, 0, 0.2)"
						},
						"&.Mui-disabled": {
							color: "#fff",
							backgroundColor: "action.disabledBackground"
						}
					}}
				>
					{isSaving ? "Saving…" : "Save and Continue"}
				</Button>
			</DialogActions>
		</CoAppDialog>
	);
}
